import { z } from 'zod';
import { RoleIdSchema, RoleRefSchema } from '../rbac/index.js';
import { nonEmptyPatch } from './_patch.js';
import { DepartmentRefSchema } from './departments.js';
import { phoneSchema } from './phone.js';

export const PasswordSchema = z.string().min(12).max(256);
export type Password = z.infer<typeof PasswordSchema>;

/**
 * @aiPurpose authoritative wire shape of a User row exposed to clients.
 * @aiInvariant `email` is lowercased; `role` is a `RoleRef` projection.
 *   `departmentId` is the writable FK; optional `department` is a read-only
 *   display projection for session/profile payloads where the backend already
 *   has authority to reveal the caller's own department label.
 *   The shape carries no shift state: live `isOnShift` is owned by
 *   `MeResponseSchema` at the top level (the only endpoint that needs
 *   it). Admin lifecycle endpoints (POST/PATCH /users, deactivate,
 *   reactivate, GET /users) intentionally do NOT surface the flag,
 *   so admin tooling cannot read or pre-position a logist's shift
 *   state — the column is owned exclusively by `PATCH /api/users/me/shift`.
 * @aiPii surfaces email, name, phone — redact in logs.
 */
export const UserSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1).max(200),
    role: RoleRefSchema,
    email: z.string().email().max(320).toLowerCase(),
    phone: phoneSchema,
    departmentId: z.string().min(1),
    department: DepartmentRefSchema.optional(),
    active: z.boolean(),
    passwordResetRequired: z.boolean(),
    createdAt: z.string().datetime(),
  })
  .strict();

/**
 * @aiPurpose typed projection of `UserSchema`.
 * @aiInvariant see `UserSchema`.
 * @aiPii surfaces email, name, phone — redact in logs.
 */
export type User = z.infer<typeof UserSchema>;

/**
 * @aiPurpose request body of `POST /api/users`.
 * @aiInvariant clients send `roleId` (branded UUID), never the legacy
 *   `role: 'admin'|'manager'|'logist_cn'` literal — backend rejects unknown
 *   roleIds with `UNKNOWN_ROLE` (400).
 * @aiPii contains email, name, phone — redact in logs.
 */
export const CreateUserInputSchema = UserSchema.omit({
  id: true,
  createdAt: true,
  passwordResetRequired: true,
  role: true,
  department: true,
})
  .extend({
    roleId: RoleIdSchema,
    password: PasswordSchema,
  })
  .strict();

/**
 * @aiPurpose typed projection of `CreateUserInputSchema`.
 * @aiInvariant see `CreateUserInputSchema`.
 * @aiPii see `CreateUserInputSchema`.
 */
export type CreateUserInput = z.infer<typeof CreateUserInputSchema>;

/**
 * @aiPurpose request body of `PATCH /api/users/:id`.
 * @aiInvariant excludes `active` (use `POST /:id/{deactivate,reactivate}`)
 *   and `passwordResetRequired` (flips only via reset-password). At least
 *   one field must be present. `roleId` is branded UUID, not a string
 *   literal — the legacy `role` field is rejected.
 * @aiPii contains email, name, phone — redact in logs.
 */
export const PatchUserInputSchema = nonEmptyPatch(
  UserSchema.omit({
    id: true,
    createdAt: true,
    active: true,
    passwordResetRequired: true,
    role: true,
    department: true,
  })
    .extend({
      roleId: RoleIdSchema,
    })
    .partial()
    .strict(),
);

/**
 * @aiPurpose typed projection of `PatchUserInputSchema`.
 * @aiInvariant see `PatchUserInputSchema`.
 * @aiPii see `PatchUserInputSchema`.
 */
export type PatchUserInput = z.infer<typeof PatchUserInputSchema>;

/**
 * @aiPurpose request body of `PATCH /api/users/me` — the authenticated user
 *   editing own profile. Allows ONLY `name` and `phone`.
 * @aiInvariant `.strict()` rejects any field outside `name`/`phone` — in
 *   particular `email`, `roleId`, `departmentId`, `active`, and
 *   `passwordResetRequired` belong to admin-only paths and must be 400 here.
 *   `name` is trimmed and must remain non-empty after trim. `phone` is the
 *   non-nullable `phoneSchema` (DB column is NOT NULL); a future migration
 *   would be required to support clearing phone via this endpoint.
 *   At least one field must be present.
 * @aiPii contains name, phone — redact in logs.
 * @aiSecurity admin-only fields are kept unreachable here; do NOT extend
 *   without a security review.
 */
export const SelfPatchUserInputSchema = nonEmptyPatch(
  z
    .object({
      name: z.string().trim().min(1).max(200).optional(),
      phone: phoneSchema.optional(),
    })
    .strict(),
);

/**
 * @aiPurpose typed projection of `SelfPatchUserInputSchema`.
 * @aiInvariant see `SelfPatchUserInputSchema`.
 * @aiPii see `SelfPatchUserInputSchema`.
 */
export type SelfPatchUserInput = z.infer<typeof SelfPatchUserInputSchema>;

/**
 * @aiPurpose request body of `PATCH /api/users/me/shift` — the
 *   authenticated `logist_cn` flipping their own `isOnShift` flag.
 * @aiInvariant `.strict()` rejects any field outside `isOnShift`. The
 *   user id is read server-side from `jwt.sub`; never accept a path,
 *   query, or body id here. Non-`logist_cn` callers receive
 *   `FORBIDDEN` from the service layer; the schema does not encode
 *   the role check (kept defense-in-depth at the boundary so a future
 *   non-logist usage cannot silently shift the predicate).
 */
export const SelfShiftPatchInputSchema = z
  .object({
    isOnShift: z.boolean(),
  })
  .strict();

export type SelfShiftPatchInput = z.infer<typeof SelfShiftPatchInputSchema>;

/**
 * @aiPurpose response shape of `PATCH /api/users/me/shift`. Carries
 *   the post-toggle `isOnShift` plus the live `isLastOnShift`
 *   indicator so the client does not have to round-trip `/me` to
 *   refresh the toggle UI.
 * @aiInvariant `isLastOnShift` is recomputed on the same transaction
 *   that updates `isOnShift`; clients never observe a stale value.
 */
export const SelfShiftResponseSchema = z
  .object({
    isOnShift: z.boolean(),
    isLastOnShift: z.boolean(),
  })
  .strict();

export type SelfShiftResponse = z.infer<typeof SelfShiftResponseSchema>;

/**
 * @aiPurpose response shape of `POST /api/users/:id/deactivate` and
 *   `POST /api/users/:id/reactivate` — the wire envelope returned by both
 *   activation toggles. `changed` is false when the user was already in the
 *   target state (idempotent no-op); the embedded `user` is the authoritative
 *   server projection after the operation.
 * @aiInvariant `.strict()` rejects unknown server-side keys at the client
 *   boundary; consumers should rely on the `user` field rather than re-deriving
 *   active-state from the request type.
 */
export const UserActivationResponseSchema = z
  .object({
    changed: z.boolean(),
    user: UserSchema,
  })
  .strict();

/**
 * @aiPurpose typed projection of `UserActivationResponseSchema`.
 * @aiInvariant see `UserActivationResponseSchema`.
 */
export type UserActivationResponse = z.infer<typeof UserActivationResponseSchema>;

/**
 * @aiPurpose query-string shape for `GET /api/users` filtering.
 * @aiInvariant `roleId` is branded UUID; the legacy `role` query param
 *   is no longer accepted. `active` accepts both boolean and the string
 *   literals `'true'`/`'false'` (URL-encoded) and normalises to boolean.
 */
export const UserListFilterSchema = z
  .object({
    roleId: RoleIdSchema.optional(),
    active: z
      .union([z.boolean(), z.enum(['true', 'false'])])
      .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
      .optional(),
    departmentId: z.string().min(1).optional(),
  })
  .strict();

/**
 * @aiPurpose typed projection of `UserListFilterSchema`.
 * @aiInvariant see `UserListFilterSchema`.
 */
export type UserListFilter = z.infer<typeof UserListFilterSchema>;
